import * as React from "react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";

export interface MultiSelectOption {
  label: string;
  value: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  value?: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  creatable?: boolean;
}

export function MultiSelect({
  options,
  value = [],
  onChange,
  placeholder = "Select items",
  className,
  disabled = false,
  creatable = false,
}: MultiSelectProps) {
  const selectedOptions = React.useMemo(() => {
    return value.map((val) => {
      const option = options.find((opt) => opt.value === val);
      return option || { label: val, value: val };
    });
  }, [options, value]);

  const handleChange = (newValue: readonly MultiSelectOption[]) => {
    onChange(newValue ? newValue.map((opt) => opt.value) : []);
  };

  const SelectComponent = creatable ? CreatableSelect : Select;

  return (
    <div className="multi-select-wrapper">
      <SelectComponent
        isMulti
        options={options}
        value={selectedOptions}
        onChange={handleChange as any}
        placeholder={placeholder}
        isDisabled={disabled}
        className={className}
        classNamePrefix="react-select"
        isClearable
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        styles={{
          control: (base) => ({
            ...base,
            minHeight: "40px",
            borderColor: "hsl(var(--input))",
            backgroundColor: "transparent",
            borderRadius: "calc(var(--radius) - 2px)",
            fontSize: "0.875rem",
            boxShadow: "none",
            "&:hover": {
              borderColor: "hsl(var(--input))",
            },
          }),
          menu: (base) => ({
            ...base,
            backgroundColor: "hsl(var(--popover))",
            border: "1px solid hsl(var(--border))",
            borderRadius: "calc(var(--radius) - 2px)",
            fontSize: "0.875rem",
            zIndex: 9999,
          }),
          menuList: (base) => ({
            ...base,
            maxHeight: "200px",
          }),
          option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected
              ? "hsl(var(--accent))"
              : state.isFocused
                ? "hsl(var(--accent) / 0.5)"
                : "transparent",
            color: "hsl(var(--popover-foreground))",
            fontSize: "0.875rem",
            cursor: "pointer",
            "&:active": {
              backgroundColor: "hsl(var(--accent))",
            },
          }),
          multiValue: (base) => ({
            ...base,
            backgroundColor: "hsl(var(--secondary))",
            borderRadius: "calc(var(--radius) - 4px)",
          }),
          multiValueLabel: (base) => ({
            ...base,
            color: "hsl(var(--secondary-foreground))",
            fontSize: "0.875rem",
            padding: "2px 6px",
          }),
          multiValueRemove: (base) => ({
            ...base,
            color: "hsl(var(--secondary-foreground))",
            cursor: "pointer",
            "&:hover": {
              backgroundColor: "hsl(var(--destructive))",
              color: "hsl(var(--destructive-foreground))",
            },
          }),
          input: (base) =>
            ({
              ...base,
              color: "hsl(var(--foreground))",
              fontSize: "0.875rem",
              boxShadow: "none !important",
              outline: "none !important",
              border: "none !important",
              "--tw-ring-shadow": "0 0 transparent",
              "--tw-ring-offset-shadow": "0 0 transparent",
            }) as any,
          placeholder: (base) => ({
            ...base,
            color: "hsl(var(--muted-foreground))",
            fontSize: "0.875rem",
          }),
        }}
      />
      <style>{`
        .multi-select-wrapper input[type='text']:focus {
          --tw-ring-shadow: 0 0 transparent !important;
          --tw-ring-offset-shadow: 0 0 transparent !important;
          box-shadow: none !important;
          outline: none !important;
        }
      `}</style>
    </div>
  );
}
